import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { apiError, getUserPermissions, updateUserPermissions } from "../../services/AiService";
import { Permission } from "../../types/ai";
import { ErrorAlert, Loading } from "./Shared";

/** ADMIN: grant or revoke QualiCheck AI / CAPA permissions of a manager (role defaults shown). */
const PermissionsModal: React.FC<{ user: { id: number; name: string } | null; onClose: () => void }> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<{ permission: Permission; granted: boolean; roleDefault: boolean }[] | null>(null);
  const [draft, setDraft] = useState<Partial<Record<Permission, boolean>>>({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setRows(null); setDraft({}); setReason(""); setError(null);
    getUserPermissions(user.id).then(setRows).catch((e) => setError(apiError(e)));
  }, [user]);

  const save = async () => {
    if (!user) return;
    try {
      await updateUserPermissions(user.id, draft, reason || undefined);
      onClose();
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <Modal show={!!user} onHide={onClose} centered className="__modal">
      <Modal.Header closeButton><Modal.Title>{t("aces.permissions.title")} — {user?.name}</Modal.Title></Modal.Header>
      <Modal.Body>
        <ErrorAlert message={error} />
        {!rows ? !error && <Loading /> : (
          <>
            {rows.map((r) => {
              const checked = draft[r.permission] ?? r.granted;
              return (
                <label key={r.permission} className="aces-check" htmlFor={`perm-${r.permission}`}>
                  <input type="checkbox" id={`perm-${r.permission}`} checked={checked}
                    onChange={(e) => setDraft({ ...draft, [r.permission]: e.target.checked })} />
                  <span>{t(`aces.permissions.names.${r.permission}`)} {r.roleDefault && <span className="aces-muted">({t("aces.permissions.default")})</span>}</span>
                </label>
              );
            })}
            <div className="aces-field mt-3">
              <label>{t("aces.common.reason")}</label>
              <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="aces-btn secondary" onClick={onClose}>{t("common.cancel")}</button>
        <button className="aces-btn" disabled={!rows || Object.keys(draft).length === 0} onClick={save}>{t("aces.common.save")}</button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionsModal;
